import { type FC } from 'react';
import type { PermissionGroup } from 'src/apis/authorization-service/types';
import type { PermissionGroupsRequest } from 'src/apis/authorization-service/use-permission-groups';
import { Card } from 'src/components/data-display/card';
import { DataItems, DataList } from 'src/components/data-display/data-list';
import { Stack } from 'src/components/layout/stack';
import { TourTip } from 'src/components/overlay/tour';
import { Heading } from 'src/components/text/heading';
import type { Successful } from 'src/hooks/use-axios';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import type { TourTipProps } from 'src/pages/settings/permission-groups/overview/page';
import { PermissionGroupActions } from 'src/pages/settings/permission-groups/overview/permission-group-actions';
import { Colors } from 'src/styles';

type Props = {
  permissionGroupsRequest: Successful<PermissionGroupsRequest>;
  permissionGroup: PermissionGroup;
  savedReferrer: SavedReferrer;
  isAdvancedModeEnabled: boolean;
};

const PermissionGroupCard: FC<Props> = ({
  permissionGroupsRequest,
  permissionGroup,
  savedReferrer,
  isAdvancedModeEnabled,
}) => {
  return (
    <Card data-testid={`permission-group-${permissionGroup.id}`}>
      <Stack gap={1} style={{ height: '100%' }} alignContent="space-between">
        <Stack>
          <Stack
            gap={1}
            alignItems="baseline"
            justifyContent="space-between"
            justifyItems="start"
            flow="column"
            align-content="space-between"
            style={{ wordBreak: 'break-word' }}
          >
            <Heading mode="card">{permissionGroup.name}</Heading>

            <PermissionGroupActions
              mode="overview"
              createLink={permissionGroupsRequest.response.data._links.create}
              permissionGroup={permissionGroup}
              savedReferrer={savedReferrer}
              onModalSuccess={() => permissionGroupsRequest.refresh?.()}
              isAdvancedModeEnabled={isAdvancedModeEnabled}
            />
          </Stack>
          {permissionGroup.isObsolete && (
            <span style={{ color: Colors.inactive }}>
              <strong>Obsolete</strong>
            </span>
          )}
          {permissionGroup.isRecommended && (
            <span style={{ color: Colors.brandSkyBlue }}>
              <strong>Recommended</strong>
            </span>
          )}
        </Stack>
        <DataList>
          <DataItems>
            <span>Assigned Users</span>
            <span>{permissionGroup.assignedUserCount}</span>
          </DataItems>
        </DataList>
      </Stack>
    </Card>
  );
};

type PermissionGroupCardsProps = {
  permissionGroupsRequest: Successful<PermissionGroupsRequest>;
  permissionGroups: PermissionGroup[];
  savedReferrer: SavedReferrer;
  dataTestId?: string;
  isAdvancedModeEnabled: boolean;
};

type PermissionGroupCardsPropsWithTour = PermissionGroupCardsProps & {
  tourTipProps: TourTipProps;
  nextTourTipStep: () => void;
};

export const PermissionGroupCards: FC<PermissionGroupCardsProps> = ({
  permissionGroups,
  permissionGroupsRequest,
  savedReferrer,
  dataTestId,
  isAdvancedModeEnabled,
}) => {
  return (
    <Stack
      gap={0.5}
      templateColumns="repeat(auto-fill, minmax(35rem, 1fr))"
      alignItems="stretch"
      data-testid={dataTestId ?? undefined}
    >
      {permissionGroups.map((permissionGroup) => (
        <PermissionGroupCard
          key={permissionGroup.id}
          permissionGroupsRequest={permissionGroupsRequest}
          permissionGroup={permissionGroup}
          savedReferrer={savedReferrer}
          isAdvancedModeEnabled={isAdvancedModeEnabled}
        />
      ))}
    </Stack>
  );
};

export const PermissionGroupCardsWithTour: FC<
  PermissionGroupCardsPropsWithTour
> = ({
  permissionGroups,
  permissionGroupsRequest,
  savedReferrer,
  dataTestId,
  isAdvancedModeEnabled,
  tourTipProps,
  nextTourTipStep,
}) => {
  const idWithTour =
    permissionGroups.find((group) => group.isRecommended)?.id ||
    permissionGroups[0].id;

  return (
    <Stack
      gap={0.5}
      templateColumns="repeat(auto-fill, minmax(35rem, 1fr))"
      alignItems="stretch"
      data-testid={dataTestId ?? undefined}
    >
      {permissionGroups.map((permissionGroup) =>
        permissionGroup.id === idWithTour ? (
          <TourTip
            key={permissionGroup.id}
            step={{
              ...tourTipProps.tourSteps[tourTipProps.currentStepIndex],
              active: tourTipProps.currentStepIndex === 3,
              isLast: tourTipProps.isLast,
              next: nextTourTipStep,
              exit: () =>
                tourTipProps.setCurrentStepIndex(tourTipProps.tourSteps.length),
            }}
          >
            <PermissionGroupCard
              permissionGroupsRequest={permissionGroupsRequest}
              permissionGroup={permissionGroup}
              savedReferrer={savedReferrer}
              isAdvancedModeEnabled={isAdvancedModeEnabled}
            />
          </TourTip>
        ) : (
          <PermissionGroupCard
            key={permissionGroup.id}
            permissionGroupsRequest={permissionGroupsRequest}
            permissionGroup={permissionGroup}
            savedReferrer={savedReferrer}
            isAdvancedModeEnabled={isAdvancedModeEnabled}
          />
        )
      )}
    </Stack>
  );
};
